'use strict'

interface AlmaWidgets {
  add: (
    paymentPlans: string,
    options: {
      container: string
      purchaseAmount: string
      hideIfNotEligible: boolean
      monochrome: boolean
      locale: string
    }
  ) => void
}

interface Alma {
  Widgets: {
    initialize: (merchantIdentifier: string, apiMode: string) => AlmaWidgets
    PaymentPlans: string
  }
  ApiMode: {
    TEST: string
    LIVE: string
  }
}

declare global {
  interface Window {
    Alma: Alma | undefined
  }
}

let loading = false
const uninitialized: HTMLElement[] = []
let almaWidgets: AlmaWidgets | null = null

const loadCustomScript = (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = url
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load script: ${url}`))
    document.head.appendChild(script)
  })
}

const loadCustomStylesheet = (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = url
    link.onload = () => resolve()
    link.onerror = () => reject(new Error(`Failed to load stylesheet: ${url}`))
    document.head.appendChild(link)
  })
}

const addWidget = (element: HTMLElement): void => {
  const id = element.id
  if (id) {
    const options = {
      container: `#${id}`,
      purchaseAmount: element.getAttribute('data-alma-amount') ?? '0',
      hideIfNotEligible: true,
      monochrome: false,
      // @TODO: Make this dynamic when deployed on different locales.
      locale: 'fr-FR',
    }
    if (almaWidgets !== null) {
      almaWidgets.add(window.Alma?.Widgets.PaymentPlans ?? '', options)
    }
  }
}

export default async (element: HTMLElement): Promise<void> => {
  if (window.Alma === undefined) {
    uninitialized.push(element)
    if (!loading) {
      loading = true
      // Load both CSS and JS files
      await Promise.all([
        loadCustomStylesheet('https://cdn.jsdelivr.net/npm/@alma/widgets@4.x/dist/widgets.min.css'),
        loadCustomScript('https://cdn.jsdelivr.net/npm/@alma/widgets@4.x/dist/widgets.umd.js'),
      ])
      loading = false

      if (window.Alma !== undefined) {
        const alma = window.Alma as Alma
        // There should be a single value for all data-alma-merchant-identifier
        // in a single page. Thus the first is used to instantiate the Alma SDK.
        const merchantIdentifier = element.getAttribute('data-alma-merchant-identifier') ?? ''
        let apiMode = alma.ApiMode.TEST
        const configuredMode = element.getAttribute('data-alma-mode') ?? ''
        if (configuredMode === 'live') {
          apiMode = alma.ApiMode.LIVE
        }
        almaWidgets = alma.Widgets.initialize(merchantIdentifier, apiMode)

        for (const element of uninitialized) {
          addWidget(element)
        }
      }
    }

    return
  }

  // Initialize the widget for this element
  addWidget(element)
}
